// The one money formatter. Pure (no imports), so the app, the extracted
// packages and the node tests all use the same code.
//
//   vi  ->  20.377.000 ₫        (dot thousands, comma decimals, ₫ after)
//   en  ->  ₫20,377,000         (what Intl gives en-US for VND)
//
// Why it exists: the DS table's `currency` formatter and `formatNumber` default
// to `1,234,567 VND` whatever the language, so after c5ca7112 the affiliate,
// contract and on-call money columns stopped looking like the old build, and
// ad-hoc `toLocaleString() + ' VND'` followed the BROWSER locale, not the
// language switch.

export type MoneyLocale = 'vi-VN' | 'en-US'

/** `vi` / `vi-VN` -> `vi-VN`; anything else -> `en-US`. */
export const moneyLocale = (lang?: string | null): MoneyLocale =>
    (lang ?? 'vi').toLowerCase().startsWith('vi') ? 'vi-VN' : 'en-US'

/**
 * Locale of the displayed language: `<html lang>` is kept in step with the
 * language switch (syncLocale in the app), so a formatter called during render
 * needs no i18n import. Defaults to Vietnamese, the app default.
 */
export const displayMoneyLocale = (): MoneyLocale =>
    moneyLocale(
        typeof document === 'undefined' ? 'vi' : document.documentElement.lang
    )

const VND_ALIASES = new Set(['', 'VND', 'Đ', 'đ', '₫'])

/** `'đ'`, `'₫'`, `''` and `null` mean VND; anything else is an ISO code. */
export const isoCurrency = (currency?: string | null): string => {
    const c = (currency ?? '').trim()
    return VND_ALIASES.has(c) ? 'VND' : c.toUpperCase()
}

/** VND has no fraction digits; any other currency shows up to 4. */
export const moneyMaxDecimals = (currency?: string | null): number =>
    isoCurrency(currency) === 'VND' ? 0 : 4

export interface MoneyOptions {
    /** ISO code, or VND alias (`đ`, `₫`, empty). @default 'VND' */
    currency?: string | null
    /** @default the displayed language (`<html lang>`) */
    locale?: MoneyLocale
    /** Fraction digits shown at most. @default 0 for VND, 4 otherwise */
    maxDecimals?: number
    /** Fraction digits always shown (`1,50` with 2). @default 0 */
    minDecimals?: number
}

/** `formatMoney(20377000) -> "20.377.000 ₫"` (vi), `"₫20,377,000"` (en). */
export function formatMoney(
    amount: number | string | null | undefined,
    { currency, locale, maxDecimals, minDecimals = 0 }: MoneyOptions = {}
): string {
    if (amount == null || amount === '') return ''
    const n = Number(amount)
    if (!Number.isFinite(n)) return String(amount)
    const code = isoCurrency(currency) || 'VND'
    const loc = locale ?? displayMoneyLocale()
    const maximumFractionDigits = Math.max(
        minDecimals,
        maxDecimals ?? moneyMaxDecimals(code)
    )
    try {
        return new Intl.NumberFormat(loc, {
            style: 'currency',
            currency: code,
            minimumFractionDigits: minDecimals,
            maximumFractionDigits,
        }).format(n)
    } catch {
        // Unknown ISO code: number + code.
        return `${formatAmount(n, { locale: loc, maxDecimals: maximumFractionDigits })} ${code}`
    }
}

/** A plain amount, no currency: `"20.377.000"` (vi) / `"20,377,000"` (en). */
export function formatAmount(
    amount: number | string | null | undefined,
    {
        locale,
        maxDecimals = 0,
    }: { locale?: MoneyLocale; maxDecimals?: number } = {}
): string {
    if (amount == null || amount === '') return ''
    const n = Number(amount)
    if (!Number.isFinite(n)) return String(amount)
    return new Intl.NumberFormat(locale ?? displayMoneyLocale(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxDecimals,
    }).format(n)
}

export interface MoneyCellOptions {
    currency: string
    position: 'prefix' | 'suffix'
    thousandSeparator: string
    decimalSeparator: string
}

/**
 * The same format as `formatMoney`, as the options of a DS table `currency`
 * formatter (which composes the string itself: symbol + separators). Read from
 * `Intl`, so it cannot drift from `formatMoney`. An empty `currency` (an
 * amount column whose header carries the unit) keeps no symbol.
 */
export function moneyCellOptions(
    currency: string | null | undefined,
    locale: MoneyLocale = displayMoneyLocale()
): MoneyCellOptions {
    const code = isoCurrency(currency)
    const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code || 'VND',
    }).formatToParts(-1234567.5)
    // Separators from a plain number: VND's own parts carry no decimal.
    const plain = new Intl.NumberFormat(locale).formatToParts(1234567.5)
    const sep = (type: string, fallback: string) =>
        plain.find((p) => p.type === type)?.value ?? fallback
    const symbol = parts.find((p) => p.type === 'currency')?.value ?? ''
    const firstNumber = parts.findIndex((p) => p.type === 'integer')
    const symbolAt = parts.findIndex((p) => p.type === 'currency')
    return {
        currency: (currency ?? '').trim() === '' ? '' : symbol,
        position: symbolAt < firstNumber ? 'prefix' : 'suffix',
        thousandSeparator: sep('group', ','),
        decimalSeparator: sep('decimal', '.'),
    }
}
